import { OutletModel, paymentsApi, PaymentsModel, ProfileModel } from '@pukka/api';
import { fileReaderUtils } from '@pukka/common/utils';
import { queryClient } from '@pukka/tanstack-query';
import { toast } from '@pukka/toast';
import { ImageUploadFileType } from '@pukka/ui-components';
import { NoInfer } from '@tanstack/react-table';
import DOMPurify from 'dompurify';
import { memoize } from 'lodash';
import { proxy } from 'valtio';
import { derive, proxySet } from 'valtio/utils';
import { z } from 'zod';

interface IGopayActivationFormState {
  step: number;
  isLoading: boolean;
  errors: string[];
}

interface IGeneralFormState {
  outletIds: number[];
  phone?: string;
  bankName?: string;
  accountNumber?: string;
  accountOwner?: string;
  businessType?: 'individual' | 'business';
  isTnCApproved: boolean;
  dirty: Set<keyof IGeneralFormState>;
}

interface IIndividualFormState {
  id?: string;
  nameById?: string;
  addressById?: string;
  permitNumber?: string;
  citizenship?: 'WNI' | 'WNA';
  dob?: string;
  cob?: string;
  gender?: 'Pria' | 'Wanita';
  job?: string;
  npwp?: string;
  nameByNpwp?: string;
  addressByNpwp?: string;
  idUpload?: [string, string];
  npwpUpload?: [string, string];
  dirty: Set<keyof IIndividualFormState>;
}

interface IBusinessFormState {
  name?: string;
  foundingCity?: string;
  foundingDate?: string;
  siup?: string;
  stakeholderName?: string;
  id?: string;
  nameById?: string;
  addressById?: string;
  directorPermitNumber?: string;
  citizenship?: 'WNI' | 'WNA';
  npwp?: string;
  nameByNpwp?: string;
  addressByNpwp?: string;
  idUpload?: [string, string];
  npwpUpload?: [string, string];
  skUpload?: [string, string];
  modifiedSkUpload?: [string, string];
  siupUpload?: [string, string];
  dirty: Set<keyof IBusinessFormState>;
}

const GopayActivationFormState = proxy<IGopayActivationFormState>({
  step: 0,
  isLoading: false,
  errors: [],
});

const GeneralFormState = proxy<IGeneralFormState>({
  outletIds: [],
  dirty: proxySet(),
  isTnCApproved: false,
  phone: '',
  bankName: '',
  accountNumber: '',
  accountOwner: '',
  businessType: undefined,
});

const IndividualFormState = proxy<IIndividualFormState>({
  id: '',
  nameById: '',
  addressById: '',
  permitNumber: '',
  citizenship: undefined,
  dob: '',
  cob: '',
  gender: undefined,
  job: '',
  npwp: '',
  nameByNpwp: '',
  addressByNpwp: '',
  dirty: proxySet(),
});

const BusinessFormState = proxy<IBusinessFormState>({
  name: '',
  foundingCity: '',
  foundingDate: '',
  siup: '',
  stakeholderName: '',
  id: '',
  nameById: '',
  addressById: '',
  directorPermitNumber: '',
  citizenship: undefined,
  npwp: '',
  nameByNpwp: '',
  addressByNpwp: '',
  dirty: proxySet(),
});

const requiredError = 'This field is required!';
const validator = {
  string: z.string({
    required_error: requiredError,
  }),
  number: z
    .string({
      required_error: requiredError,
    })
    .regex(/^[0-9]+$/, 'It must be a number!'),
  phone: z
    .string({
      required_error: requiredError,
    })
    .regex(/^([+]?[\s0-9]+)?(\d{3}|[(]?[0-9]+[)])?([-]?[\s]?[0-9])+$/, 'It must be in phone number format!'),
  date: z
    .string({
      required_error: requiredError,
    })
    .date(),
  url: z
    .string({
      required_error: requiredError,
    })
    .array()
    .length(2),
};

const ValidationState = derive({
  generalValidation: (get) => {
    const { accountNumber, accountOwner, bankName, businessType, phone, outletIds, isTnCApproved } = get(GeneralFormState);

    return {
      outletIds: { success: outletIds.length > 0 },
      phone: validator.phone.min(1, requiredError).safeParse(phone),
      bankName: validator.string.min(1, requiredError).safeParse(bankName),
      accountNumber: validator.number.min(1, requiredError).safeParse(accountNumber),
      accountOwner: validator.string.min(1, requiredError).safeParse(accountOwner),
      businessType: validator.string.min(1, requiredError).safeParse(businessType),
      isTnCApproved,
    };
  },
  individualValidation: (get) => {
    const {
      id,
      nameById,
      addressById,
      permitNumber,
      citizenship,
      dob,
      cob,
      gender,
      job,
      npwp,
      nameByNpwp,
      addressByNpwp,
      idUpload,
      npwpUpload,
    } = get(IndividualFormState);

    return {
      id: validator.number.min(1, requiredError).safeParse(id),
      nameById: validator.string.min(1, requiredError).safeParse(nameById),
      addressById: validator.string.min(1, requiredError).safeParse(addressById),
      permitNumber: validator.string.optional().safeParse(permitNumber),
      citizenship: validator.string.min(1, requiredError).safeParse(citizenship),
      dob: validator.date.safeParse(dob),
      cob: validator.string.min(1, requiredError).safeParse(cob),
      gender: validator.string.min(1, requiredError).safeParse(gender),
      job: validator.string.min(1, requiredError).safeParse(job),
      npwp: validator.string.optional().safeParse(npwp),
      nameByNpwp: validator.string.optional().safeParse(nameByNpwp),
      addressByNpwp: validator.string.optional().safeParse(addressByNpwp),
      idUpload: validator.url.min(1, requiredError).safeParse(idUpload),
      npwpUpload: validator.url.min(1, requiredError).safeParse(npwpUpload),
    };
  },
  businessValidation: (get) => {
    const {
      name,
      foundingCity,
      foundingDate,
      siup,
      stakeholderName,
      id,
      nameById,
      addressById,
      directorPermitNumber,
      citizenship,
      npwp,
      nameByNpwp,
      addressByNpwp,
      idUpload,
      npwpUpload,
      siupUpload,
      skUpload,
      modifiedSkUpload,
    } = get(BusinessFormState);

    return {
      name: validator.string.min(1, requiredError).safeParse(name),
      foundingCity: validator.string.min(1, requiredError).safeParse(foundingCity),
      foundingDate: validator.date.min(1, requiredError).safeParse(foundingDate),
      siup: validator.number.min(1, requiredError).safeParse(siup),
      stakeholderName: validator.string.min(1, requiredError).safeParse(stakeholderName),
      id: validator.number.min(1, requiredError).safeParse(id),
      nameById: validator.string.min(1, requiredError).safeParse(nameById),
      addressById: validator.string.min(1, requiredError).safeParse(addressById),
      directorPermitNumber: validator.string.optional().safeParse(directorPermitNumber),
      citizenship: validator.string.min(1, requiredError).safeParse(citizenship),
      npwp: validator.number.min(1, requiredError).safeParse(npwp),
      nameByNpwp: validator.string.min(1, requiredError).safeParse(nameByNpwp),
      addressByNpwp: validator.string.min(1, requiredError).safeParse(addressByNpwp),
      idUpload: validator.url.min(1, requiredError).safeParse(idUpload),
      skUpload: validator.url.min(1, requiredError).safeParse(skUpload),
      npwpUpload: validator.url.min(1, requiredError).safeParse(npwpUpload),
      siupUpload: validator.url.min(1, requiredError).safeParse(siupUpload),
      modifiedSkUpload: validator.url.min(1, requiredError).safeParse(modifiedSkUpload),
    };
  },
});

function isComplete(get: <T extends object>(params: T) => T) {
  const { step } = get(GopayActivationFormState);

  switch (step) {
    case 1: {
      const { businessType } = get(GeneralFormState);

      if (businessType === 'individual') {
        const {
          individualValidation: {
            id,
            nameById,
            addressById,
            permitNumber,
            citizenship,
            dob,
            cob,
            gender,
            job,
            npwp,
            nameByNpwp,
            addressByNpwp,
          },
        } = get(ValidationState);

        return (
          id.success &&
          nameById.success &&
          addressById.success &&
          permitNumber.success &&
          citizenship.success &&
          dob.success &&
          cob.success &&
          gender.success &&
          job.success &&
          npwp.success &&
          nameByNpwp.success &&
          addressByNpwp.success
        );
      }

      const {
        businessValidation: {
          name,
          foundingCity,
          foundingDate,
          siup,
          stakeholderName,
          id,
          nameById,
          addressById,
          directorPermitNumber,
          citizenship,
          npwp,
          nameByNpwp,
          addressByNpwp,
        },
      } = get(ValidationState);

      return (
        name.success &&
        foundingCity.success &&
        foundingDate.success &&
        siup.success &&
        stakeholderName.success &&
        id.success &&
        nameById.success &&
        addressById.success &&
        directorPermitNumber.success &&
        citizenship.success &&
        npwp.success &&
        nameByNpwp.success &&
        addressByNpwp.success
      );
    }
    case 2: {
      const { businessType } = get(GeneralFormState);

      if (businessType === 'individual') {
        const {
          individualValidation: { idUpload, npwpUpload },
        } = get(ValidationState);

        return idUpload.success && npwpUpload.success;
      }

      const {
        businessValidation: { idUpload, skUpload, npwpUpload, siupUpload, modifiedSkUpload },
      } = get(ValidationState);

      return idUpload.success && skUpload.success && npwpUpload.success && siupUpload.success && modifiedSkUpload.success;
    }
    case 3: {
      return get(GeneralFormState).isTnCApproved;
    }
    case 0:
    default: {
      const {
        generalValidation: { accountNumber, accountOwner, bankName, businessType, phone, outletIds },
      } = get(ValidationState);

      return (
        outletIds.success && phone.success && bankName.success && accountNumber.success && accountOwner.success && businessType.success
      );
    }
  }
}

const DerivedState = derive({
  isComplete,
});

type uploadKeys<T> = T extends `${infer S}Upload` ? T & `${S}Upload` : never;
type nonUploadKeys<T> = T extends `${string}Upload` ? never : T;

const onChangeValue = memoize(
  <
    T extends 'general' | 'business' | 'individual',
    S extends T extends 'individual' ? IIndividualFormState : T extends 'business' ? IBusinessFormState : IGeneralFormState,
  >(
    type: T,
    key: NoInfer<nonUploadKeys<keyof S>>,
  ) => {
    return (value: string | boolean | number[]) => {
      const sanitizedValue = (typeof value === 'string' ? DOMPurify.sanitize(value) : value) as S[nonUploadKeys<keyof S>];

      switch (type) {
        case 'individual':
          (IndividualFormState as S)[key] = sanitizedValue;
          IndividualFormState.dirty.add(key as keyof IIndividualFormState);
          break;
        case 'business':
          (BusinessFormState as S)[key] = sanitizedValue;
          BusinessFormState.dirty.add(key as keyof IBusinessFormState);
          break;
        case 'general':
        default:
          (GeneralFormState as S)[key] = sanitizedValue;
          GeneralFormState.dirty.add(key as keyof IGeneralFormState);
          break;
      }
    };
  },
  (type, key) => `${type}|${key}`,
);

const onRemoveFile = memoize(
  <T extends 'business' | 'individual', S extends T extends 'individual' ? IIndividualFormState : IBusinessFormState>(
    type: T,
    key: NoInfer<uploadKeys<keyof S>>,
  ) => {
    return () => {
      switch (type) {
        case 'individual':
          (IndividualFormState as S)[key] = undefined as S[uploadKeys<keyof S>];
          IndividualFormState.dirty.add(key as keyof IIndividualFormState);
          break;
        case 'business':
        default:
          (BusinessFormState as S)[key] = undefined as S[uploadKeys<keyof S>];
          BusinessFormState.dirty.add(key as keyof IBusinessFormState);
          break;
      }
    };
  },
  (type, key) => `${type}|${key}`,
);

const onUpload = memoize(
  <T extends 'business' | 'individual', S extends T extends 'individual' ? IIndividualFormState : IBusinessFormState>(
    type: T,
    key: NoInfer<uploadKeys<keyof S>>,
  ) => {
    return (files: ImageUploadFileType<unknown>[]) => {
      switch (type) {
        case 'individual':
          (IndividualFormState as S)[key] = (
            files.length > 0 && !files[0].error ? [files[0].name, files[0].url] : undefined
          ) as S[uploadKeys<keyof S>];
          IndividualFormState.dirty.add(key as keyof IIndividualFormState);
          break;
        case 'business':
        default:
          (BusinessFormState as S)[key] = (files.length > 0 && !files[0].error ? [files[0].name, files[0].url] : undefined) as S[uploadKeys<
            keyof S
          >];

          BusinessFormState.dirty.add(key as keyof IBusinessFormState);
          break;
      }
    };
  },
  (type, key) => `${type}|${key}`,
);

function onPreviousStep() {
  switch (GopayActivationFormState.step) {
    case 0:
      break;
    case 1:
    case 2:
    case 3:
    default:
      GopayActivationFormState.step -= 1;
      break;
  }
}

function stripTypeFromBase64(base64?: string) {
  return base64?.split(',')[1];
}

function generateIndividualPayload(
  profileData: ProfileModel.Response,
  outlets: OutletModel.Outlet[],
  GeneralState = GeneralFormState,
  IndividualState = IndividualFormState,
) {
  const businessId = profileData.business?.id;
  const selectedOutlets = outlets?.filter((outlet) => GeneralState.outletIds.includes(outlet.id));

  const ktpBase64 = stripTypeFromBase64(IndividualState.idUpload?.[1]);
  const ktpImgType = fileReaderUtils.getImageTypeFromBase64(IndividualState.idUpload?.[1]);

  const npwpBase64 = stripTypeFromBase64(IndividualState.npwpUpload?.[1]);
  const npwpImgType = fileReaderUtils.getImageTypeFromBase64(IndividualState.npwpUpload?.[1]);

  const payload: PaymentsModel.ActivationRequestPayload = {
    outlets: selectedOutlets,
    ktp_number: IndividualState.id,
    ktp_name: IndividualState.nameById,
    ktp_address: IndividualState.addressById,
    nationality: IndividualState.citizenship,
    sex: IndividualState.gender,
    npwp_name: IndividualState.nameByNpwp,
    npwp_number: IndividualState.npwp,
    npwp_address: IndividualState.addressByNpwp,
    bank_name: GeneralState.bankName,
    bank_account_number: GeneralState.accountNumber,
    bank_account_owner: GeneralState.accountOwner,
    business_name: profileData.business?.name,
    business_email: profileData.business?.email,
    moka_registered_email: profileData.email,
    merchant_pic_phone: GeneralState.phone,
    type_of_business: 'INDIVIDUAL',
    permit_number: IndividualState.permitNumber,
    ktp: {
      base64: ktpBase64,
      file_name: `KTP-${businessId}.${ktpImgType}`,
    },
    npwp: {
      base64: npwpBase64,
      file_name: `NPWP-${businessId}.${npwpImgType}`,
    },
    place_of_birth: IndividualState.cob,
    date_of_birth: IndividualState.dob,
    occupation: IndividualState.job,
  };

  return payload;
}

function generateBusinessPayload(
  profileData: ProfileModel.Response,
  outlets: OutletModel.Outlet[],
  GeneralState = GeneralFormState,
  BusinessState = BusinessFormState,
) {
  const businessId = profileData.business?.id;
  const selectedOutlets = outlets?.filter((outlet) => GeneralState.outletIds.includes(outlet.id));

  const ktpBase64 = stripTypeFromBase64(BusinessState.idUpload?.[1]);
  const ktpImgType = fileReaderUtils.getImageTypeFromBase64(BusinessState.idUpload?.[1]);

  const npwpBase64 = stripTypeFromBase64(BusinessState.npwpUpload?.[1]);
  const npwpImgType = fileReaderUtils.getImageTypeFromBase64(BusinessState.npwpUpload?.[1]);

  const skBase64 = stripTypeFromBase64(BusinessState.skUpload?.[1]);

  const modifiedSkBase64 = stripTypeFromBase64(BusinessState.modifiedSkUpload?.[1]);

  const siupBase64 = stripTypeFromBase64(BusinessState.siupUpload?.[1]);
  const siupImgType = fileReaderUtils.getImageTypeFromBase64(BusinessState.siupUpload?.[1]);

  const payload: PaymentsModel.ActivationRequestPayload = {
    outlets: selectedOutlets,
    ktp_number: BusinessState.id,
    ktp_name: BusinessState.nameById,
    ktp_address: BusinessState.addressById,
    nationality: BusinessState.citizenship,
    npwp_name: BusinessState.nameByNpwp,
    npwp_number: BusinessState.npwp,
    npwp_address: BusinessState.addressByNpwp,
    bank_name: GeneralState.bankName,
    bank_account_number: GeneralState.accountNumber,
    bank_account_owner: GeneralState.accountOwner,
    business_name: profileData.business?.name,
    business_email: profileData.business?.email,
    moka_registered_email: profileData.email,
    merchant_pic_phone: GeneralState.phone,
    type_of_business: 'CORPORATE',
    permit_number: BusinessState.directorPermitNumber,
    business_entity_name: BusinessState.name,
    business_entity_establishment_city: BusinessState.foundingCity,
    business_entity_permit: BusinessState.siup,
    business_shareholder_name: BusinessState.stakeholderName,
    business_entity_establishment_date: BusinessState.foundingDate,
    ktp: {
      base64: ktpBase64,
      file_name: `KTP-${businessId}.${ktpImgType}`,
    },
    npwp: {
      base64: npwpBase64,
      file_name: `NPWP-${businessId}.${npwpImgType}`,
    },
    sk_pendirian: {
      base64: skBase64,
      file_name: `SKPendirian-${businessId}.pdf`,
    },
    sk_perubahan: {
      base64: modifiedSkBase64,
      file_name: `SKPerubahan-${businessId}.pdf`,
    },
    siup: {
      base64: siupBase64,
      file_name: `SIUP-${businessId}.${siupImgType}`,
    },
  };

  return payload;
}

function resetAllState() {
  GopayActivationFormState.step = 0;
  GopayActivationFormState.isLoading = false;
  GopayActivationFormState.errors = [];

  GeneralFormState.outletIds = [];
  GeneralFormState.dirty = proxySet();
  GeneralFormState.isTnCApproved = false;
  GeneralFormState.phone = '';
  GeneralFormState.bankName = '';
  GeneralFormState.accountNumber = '';
  GeneralFormState.accountOwner = '';
  GeneralFormState.businessType = undefined;

  IndividualFormState.id = '';
  IndividualFormState.nameById = '';
  IndividualFormState.addressById = '';
  IndividualFormState.permitNumber = '';
  IndividualFormState.citizenship = undefined;
  IndividualFormState.dob = '';
  IndividualFormState.cob = '';
  IndividualFormState.gender = undefined;
  IndividualFormState.job = '';
  IndividualFormState.npwp = '';
  IndividualFormState.nameByNpwp = '';
  IndividualFormState.addressByNpwp = '';
  IndividualFormState.dirty = proxySet();

  BusinessFormState.name = '';
  BusinessFormState.foundingCity = '';
  BusinessFormState.foundingDate = '';
  BusinessFormState.siup = '';
  BusinessFormState.stakeholderName = '';
  BusinessFormState.id = '';
  BusinessFormState.nameById = '';
  BusinessFormState.addressById = '';
  BusinessFormState.directorPermitNumber = '';
  BusinessFormState.citizenship = undefined;
  BusinessFormState.npwp = '';
  BusinessFormState.nameByNpwp = '';
  BusinessFormState.addressByNpwp = '';
  BusinessFormState.dirty = proxySet();
}

function onSubmitForm(
  profileData: ProfileModel.Response,
  outlets: OutletModel.Outlet[],
  onSubmitSuccess?: () => void,
  GeneralState = GeneralFormState,
  GopayActivationState = GopayActivationFormState,
  BusinessState = BusinessFormState,
  IndividualState = IndividualFormState,
  Derived = DerivedState,
  setter: (payload: PaymentsModel.ActivationRequestPayload) => Promise<unknown> = paymentsApi.activatePaymentMethod,
) {
  const { businessType } = GeneralState;

  switch (GopayActivationState.step) {
    case 3:
      if (GeneralState.isTnCApproved) {
        GopayActivationState.isLoading = true;
        GopayActivationState.errors = [];

        Promise.resolve()
          .then(async () => {
            const payload =
              businessType === 'business'
                ? generateBusinessPayload(profileData, outlets, GeneralState, BusinessState)
                : generateIndividualPayload(profileData, outlets, GeneralState, IndividualState);

            await setter(payload);

            toast.success('Your registration was successfully submitted');

            resetAllState();

            onSubmitSuccess?.();

            queryClient.invalidateQueries({ queryKey: ['payment-methods'] });
          })
          .catch((e) => {
            console.error(e);

            if (e.response?.status === 400 && Array.isArray(e.response?.data)) {
              //  error from salesforce validation
              toast.error('Failed to submit. Please check your submission');

              GopayActivationState.errors = e.response.data;
            } else if (e.response?.data?.message) {
              // error from moka validation
              toast.error(e.response?.data?.message);
            } else {
              toast.error('Something went wrong. Please try again later');
            }
          })
          .finally(() => {
            GopayActivationState.isLoading = false;
          });
      }

      break;
    // @ts-ignore
    case 2:
      if (businessType === 'business') {
        BusinessState.dirty.add('idUpload');
        BusinessState.dirty.add('npwpUpload');
        BusinessState.dirty.add('skUpload');
        BusinessState.dirty.add('modifiedSkUpload');
        BusinessState.dirty.add('siupUpload');
      } else {
        IndividualState.dirty.add('idUpload');
        IndividualState.dirty.add('npwpUpload');
      }
    // @ts-ignore
    // eslint-disable-next-line no-fallthrough
    case 1:
      if (businessType === 'business') {
        BusinessState.dirty.add('addressById');
        BusinessState.dirty.add('addressByNpwp');
        BusinessState.dirty.add('citizenship');
        BusinessState.dirty.add('directorPermitNumber');
        BusinessState.dirty.add('foundingCity');
        BusinessState.dirty.add('foundingDate');
        BusinessState.dirty.add('id');
        BusinessState.dirty.add('name');
        BusinessState.dirty.add('nameById');
        BusinessState.dirty.add('nameByNpwp');
        BusinessState.dirty.add('npwp');
        BusinessState.dirty.add('siup');
        BusinessState.dirty.add('stakeholderName');
      } else {
        IndividualState.dirty.add('addressById');
        IndividualState.dirty.add('addressByNpwp');
        IndividualState.dirty.add('citizenship');
        IndividualState.dirty.add('cob');
        IndividualState.dirty.add('dob');
        IndividualState.dirty.add('gender');
        IndividualState.dirty.add('id');
        IndividualState.dirty.add('job');
        IndividualState.dirty.add('nameById');
        IndividualState.dirty.add('nameByNpwp');
        IndividualState.dirty.add('npwp');
        IndividualState.dirty.add('permitNumber');
      }
    // @ts-ignore
    // eslint-disable-next-line no-fallthrough
    case 0:
      GeneralState.dirty.add('accountNumber');
      GeneralState.dirty.add('accountOwner');
      GeneralState.dirty.add('bankName');
      GeneralState.dirty.add('businessType');
      GeneralState.dirty.add('outletIds');
      GeneralState.dirty.add('phone');
    // eslint-disable-next-line no-fallthrough
    default:
      if (Derived.isComplete) {
        GopayActivationState.step += 1;
      }

      break;
  }
}

function onRemoveOutlet(outletId: number) {
  GeneralFormState.outletIds = GeneralFormState.outletIds.filter((oId) => oId !== outletId);
}

function getFileValue(value?: readonly [string, string]) {
  if (value) {
    return [
      {
        name: value[0],
        url: value[1],
      },
    ];
  }

  return value;
}

export {
  GopayActivationFormState,
  IndividualFormState,
  BusinessFormState,
  GeneralFormState,
  ValidationState,
  DerivedState,
  onUpload,
  onPreviousStep,
  onSubmitForm,
  onRemoveOutlet,
  getFileValue,
  onChangeValue,
  isComplete as _isComplete,
  onRemoveFile,
  generateBusinessPayload,
  generateIndividualPayload,
  resetAllState,
};
